import {
  getCurrentFile,
  getRegenerateExtraction,
  useAppSelector
} from 'app/redux';
import * as React from 'react';
import { SetStateAction, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import styles from './PDFHighlightPreview.module.scss';
import { Link } from '@zurich-base-ui/components.link';

export function PDFHighlightPreview({
  highlightCoords,
  selectedHighlight,
  showHighlights,
  displayFile
}: {
  highlightCoords: number | undefined;
  selectedHighlight: number | undefined;
  showHighlights: boolean;
  displayFile?: File;
}): React.JSX.Element {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    `pdf.worker.min.js`,
    window.location.origin
  ).toString();

  const file = useAppSelector(getCurrentFile);
  const regenerate = useAppSelector(getRegenerateExtraction);

  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);

  function onDocumentLoadSuccess(pdf: {
    numPages: SetStateAction<number>;
  }): void {
    setNumPages(pdf.numPages);
    setPageNumber(1);
  }

  function handleSetPageNumber(pageNumber: number): void {
    let newPage = pageNumber;

    if (newPage > numPages) {
      newPage = numPages;
    }
    if (newPage < 1) {
      newPage = 1;
    }

    setPageNumber(newPage);
  }

  useEffect(() => {
    setPageNumber(1);
  }, [regenerate]);

  useEffect(() => {
    let minPageDifference: number | null = null;
    let closestPage = 1;

    if (
      highlightCoords != null &&
      selectedHighlight != null &&
      Object.values(highlightCoords)[selectedHighlight] !== undefined
    ) {
      (Object as any)
        .values(highlightCoords)
        [selectedHighlight].map((coord: { page_nr: number }) => {
          if (
            minPageDifference == null ||
            minPageDifference > Math.abs(coord.page_nr - pageNumber)
          ) {
            minPageDifference = Math.abs(coord.page_nr - pageNumber);
            closestPage = coord.page_nr;
          }
          setPageNumber(closestPage);
          return true;
        });
      setPageNumber(closestPage);
    }
  }, [selectedHighlight]);

  return (
    <div>
      {showHighlights && (
        <div className={styles['row-pages']}>
          {highlightCoords !== null &&
            selectedHighlight !== undefined &&
            highlightCoords !== undefined &&
            Object.values(highlightCoords)[selectedHighlight] !== undefined &&
            (Object as any).values(highlightCoords)[selectedHighlight].map(
              (
                coord: {
                  page_nr: SetStateAction<number>;
                  color: string | undefined;
                },
                index: number
              ) => {
                return (
                  <div
                    key={coord.page_nr.toString() + String(index)}
                    style={{
                      borderRadius: '100px',
                      width: '32px',
                      height: '32px',
                      padding: 0,
                      margin: 2,
                      flex: '0 0 0 0',
                      backgroundColor:
                        coord.page_nr === pageNumber ? '#0F67BF' : '#f7f8f8',
                      justifyContent: 'center',
                      display: 'flex'
                    }}
                  >
                    <Link
                      className={styles['nav-pdf-bar']}
                      onClick={() => setPageNumber(coord.page_nr)}
                    >
                      <p
                        style={{
                          margin: 0,
                          color:
                            coord.page_nr === pageNumber ? 'white' : '#0F67BF'
                        }}
                      >
                        {coord.page_nr.toString()}
                      </p>
                    </Link>
                  </div>
                );
              }
            )}
        </div>
      )}
      <div className={styles['pdf-center']}>
        <Document
          className={styles.document}
          file={displayFile || file}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          <Page className={styles.page} pageNumber={pageNumber}>
            {highlightCoords != null &&
              showHighlights &&
              selectedHighlight != null &&
              Object.values(highlightCoords)[selectedHighlight] !== undefined &&
              (Object as any)
                .values(highlightCoords)
                [selectedHighlight].filter(
                  (coord: { page_nr: number }) => coord.page_nr === pageNumber
                )
                .map(
                  (coord: { full_coord: string; color: any }, index: any) => {
                    const vertices = coord.full_coord
                      .split('[')
                      .join()
                      .split(']')
                      .join()
                      .split('(')
                      .join()
                      .split(')')
                      .join()
                      .split(',')
                      .map((vertex: string) => vertex.trim())
                      .filter((v: string | any[]) => v.length);
                    const curCoord = {
                      x0: parseFloat(vertices[0]),
                      y0: parseFloat(vertices[1]),
                      x1: parseFloat(vertices[2]),
                      y1: parseFloat(vertices[5])
                    };
                    return (
                      <div
                        key={coord.full_coord + String(index)}
                        style={{
                          position: 'absolute',
                          bottom: curCoord.y0,
                          left: curCoord.x0,
                          width: curCoord.x1 - curCoord.x0,
                          height: curCoord.y1 - curCoord.y0,
                          background: coord.color || 'yellow',
                          opacity: 0.3
                        }}
                      />
                    );
                  }
                )}
          </Page>
        </Document>

        {(displayFile || file) && (
          <div className={styles['num-pages-small']}>
            {
              <Link
                className={styles['next-prev-button']}
                onClick={() => handleSetPageNumber(pageNumber - 1)}
              >
                {'<'}
              </Link>
            }
            <div className={styles['pages-box']}>
              <input
                type="text"
                inputMode="numeric"
                value={pageNumber}
                style={{
                  backgroundColor: '#0F67BF',
                  color: 'white',
                  border: 0,
                  height: '32px',
                  width: '32px',
                  borderRadius: '100px',
                  textAlign: 'center',
                  fontSize: '1rem'
                }}
                onChange={(v) => handleSetPageNumber(Number(v.target.value))}
              />
              <div className={styles['page-item']}>/</div>
              <div className={styles['page-item']}>{numPages}</div>
            </div>

            {
              <Link
                className={styles['next-prev-button']}
                onClick={() => handleSetPageNumber(pageNumber + 1)}
              >
                {'>'}
              </Link>
            }
          </div>
        )}
      </div>
    </div>
  );
}
